import { useRef, useState } from 'react'
import { Settings2 } from 'lucide-react'
import { cn } from '@/lib/cn'
import { useClickOutside } from '@/lib/useClickOutside'
import { useFieldDefinitions } from '@/features/contacts/useContacts'
import {
  BUILTIN_CARD_FIELDS,
  MAX_CARD_FIELDS,
  customCardFields,
  type CardFieldDef,
  type CardSettings,
} from '@/features/board/cardSettings'

function SectionTitle({ children }: { children: string }) {
  return (
    <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-1.5">
      {children}
    </div>
  )
}

/**
 * Gear button + popover with card-field settings: which deal fields (built-in and
 * custom) are shown on board cards. Shown next to the Board | Table toggle.
 */
export default function CardSettingsPopover({
  settings,
  onChange,
}: {
  settings: CardSettings
  onChange: (next: CardSettings) => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useClickOutside(ref, () => setOpen(false), open)
  const { data: dealFieldDefs } = useFieldDefinitions('deal')
  const customFields = customCardFields(dealFieldDefs)
  const atLimit = settings.fields.length >= MAX_CARD_FIELDS

  function toggleField(id: string) {
    if (settings.fields.includes(id)) {
      onChange({ ...settings, fields: settings.fields.filter((f) => f !== id) })
    } else if (!atLimit) {
      onChange({ ...settings, fields: [...settings.fields, id] })
    }
  }

  function renderCheckbox(field: CardFieldDef) {
    const checked = settings.fields.includes(field.id)
    const disabled = !checked && atLimit
    return (
      <label
        key={field.id}
        className={cn(
          'flex items-center gap-2 text-sm',
          disabled ? 'text-gray-400' : 'text-brand-dark cursor-pointer',
        )}
      >
        <input
          type="checkbox"
          disabled={disabled}
          checked={checked}
          onChange={() => toggleField(field.id)}
          className="accent-brand-primary"
        />
        {field.label}
      </label>
    )
  }

  return (
    <div
      ref={ref}
      className="relative"
      onKeyDown={(e) => {
        if (e.key === 'Escape') setOpen(false)
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label="Card settings"
        title="Card settings"
        className={cn(
          'p-2 rounded-lg border transition-colors',
          open
            ? 'border-brand-primary/40 bg-brand-primary/10 text-brand-dark'
            : 'border-gray-200 bg-white text-gray-500 hover:text-brand-dark',
        )}
      >
        <Settings2 size={15} />
      </button>

      {open && (
        <div
          className="absolute right-0 z-30 mt-1 w-60 bg-white border border-gray-200 rounded-xl shadow-lg p-4 space-y-4"
        >
          <section>
            <SectionTitle>Photo</SectionTitle>
            <label className="flex items-center gap-2 text-sm text-brand-dark cursor-pointer">
              <input
                type="checkbox"
                checked={settings.showPhoto}
                onChange={(e) => onChange({ ...settings, showPhoto: e.target.checked })}
                className="accent-brand-primary"
              />
              Show the deal’s photo
            </label>
          </section>

          <section>
            <SectionTitle>Card fields</SectionTitle>
            <p className="text-[11px] text-gray-400 mb-1.5">
              Title and status are always shown · up to {MAX_CARD_FIELDS} fields
            </p>
            <div className="space-y-1">{BUILTIN_CARD_FIELDS.map(renderCheckbox)}</div>
          </section>

          {customFields.length > 0 && (
            <section>
              <SectionTitle>Custom fields</SectionTitle>
              <div className="space-y-1">{customFields.map(renderCheckbox)}</div>
            </section>
          )}
        </div>
      )}
    </div>
  )
}
